import { useEffect, useState } from "react";
import { Accordion, Card, Col, Row } from "react-bootstrap";
import { listFounderStories } from "../services/api";

const coreValues = [
  {
    title: "Service Before Self",
    body: "We honor the enduring legacy of military service by placing the needs of veterans and their families at the forefront of everything we do. Our organization is built on the belief that service does not end when the uniform comes off. Service Before Self means we approach every program, partnership, and decision with a mission-first mindset, listening actively to veteran needs and designing responsive, high-impact solutions.",
  },
  {
    title: "Integrity and Accountability",
    body: "We operate with the highest standards of honesty, transparency, and ethical leadership. Trust is the foundation of our relationships. We commit to transparent financial reporting, measurable outcomes, and data-informed decisions. We hold ourselves accountable not only for how funds are spent, but for the tangible difference we create in the lives of those we serve.",
  },
  {
    title: "Empowerment Through Opportunity",
    body: "We believe every veteran possesses valuable skills, leadership experience, resilience, and untapped potential. Rather than focusing on deficits, we focus on strengths. Our programs provide pathways for long-term independence and growth, designed not just to assist, but to elevate and help veterans thrive personally, professionally, and socially.",
  },
  {
    title: "Brotherhood, Sisterhood, and Community",
    body: "Military service creates bonds that last a lifetime. We recognize the power of shared experience and the importance of connection in healing and growth. We foster a strong, inclusive community where veterans support, mentor, and uplift one another. We strive to make sure veterans have community support when facing hardship.",
  },
];

const missionPromises = [
  {
    title: "For Those Struggling in Silence",
    body: "We offer compassionate support and a listening ear.",
  },
  {
    title: "For Those Facing Uncertainty",
    body: "We help connect you to clear next steps and trusted resources.",
  },
  {
    title: "For Those Experiencing Hardship",
    body: "We work to provide support with dignity and respect.",
  },
];

function AboutPage() {
  const [founderStories, setFounderStories] = useState([]);

  useEffect(() => {
    const loadFounderStories = async () => {
      try {
        const response = await listFounderStories();
        setFounderStories(response.data || []);
      } catch {
        setFounderStories([]);
      }
    };

    loadFounderStories();
  }, []);

  return (
    <section>
      <Card className="glass-card text-light mb-4 border-warning">
        <Card.Body>
          <h1 className="section-title mb-3">About Fremont Veterans Group</h1>
          <p className="text-muted-soft lead mb-0">Dedicated to providing compassionate support and practical guidance to veterans and their families in Fremont County and beyond.</p>
        </Card.Body>
      </Card>

      <Card className="glass-card text-light mb-4">
        <Card.Body>
          <h2 className="h4 mb-3">Our Mission</h2>
          <p className="text-light mb-3" style={{ fontSize: "1.1rem" }}>
            <span className="text-warning fw-bold">Our mission is born from gratitude and guided by compassion.</span>{" "}
            We stand beside the men and women who once stood for us, connecting veterans to practical resources, offering steady guidance through difficult seasons, and working with community partners to reduce food insecurity.
          </p>
          <p className="text-muted-soft mb-3">
            As a small local group, we focus on meeting veterans where they are and walking with them toward greater stability. From emergency assistance to benefits navigation and trusted referrals, we aim to reduce barriers and support veterans as they move forward with dignity.
          </p>
          <Row className="g-3">
            {missionPromises.map((promise) => (
              <Col md={4} key={promise.title}>
                <Card className="h-100 border-0 bg-transparent border-start border-3 border-info">
                  <Card.Body className="py-2">
                    <h3 className="h6 text-info mb-2">{promise.title}</h3>
                    <p className="text-muted-soft mb-0">{promise.body}</p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
          <p className="text-muted-soft mt-3 mb-0">Service should never be forgotten, and every veteran deserves consistent community support.</p>
        </Card.Body>
      </Card>

      <Card className="glass-card text-light mb-4">
        <Card.Body>
          <h2 className="h4 mb-3">Who We Are</h2>
          <p className="text-muted-soft mb-3">
            The Fremont County Veterans Group is a dedicated community of individuals committed to assisting veterans as they navigate challenges commonly faced after military service. We offer vital guidance and practical aid, addressing issues such as housing, food insecurity, benefits navigation,
            and bill payments.
          </p>
          <p className="text-muted-soft mb-0">
            By connecting veterans to specialized resources and support networks, we play a crucial role in fostering stability and well-being among those who have served. We understand that many veterans find it overwhelming to navigate complex benefit systems or locate reliable local aid, so we
            bridge that gap, ensuring personalized support that directly addresses individual circumstances.
          </p>
        </Card.Body>
      </Card>

      {founderStories.length > 0 && (
        <Card className="glass-card text-light mb-4">
          <Card.Body>
            <h2 className="h4 mb-3">Why we started Fremont Veterans Group</h2>
            <Row className="g-3">
              {founderStories.map((story) => (
                <Col md={6} key={story.id}>
                  <Card className="h-100 resource-card text-light">
                    <Card.Body>
                      <p className="text-muted-soft mb-3">{story.storyText}</p>
                      <p className="mb-0 text-warning fw-semibold">
                        - {story.founderName}
                        {story.founderTitle ? `, ${story.founderTitle}` : ""}
                      </p>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card.Body>
        </Card>
      )}

      <Card className="glass-card text-light mb-4">
        <Card.Body>
          <h2 className="h4 mb-3">Our Core Values</h2>
          <Accordion flush alwaysOpen>
            {coreValues.map((value, idx) => (
              <Accordion.Item eventKey={idx.toString()} key={value.title} className="home-accordion-item">
                <Accordion.Header className="fw-semibold text-light">{value.title}</Accordion.Header>
                <Accordion.Body className="text-muted-soft">{value.body}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Card.Body>
      </Card>

      <Row className="g-3 mb-4">
        <Col md={6}>
          <Card className="glass-card h-100 text-light">
            <Card.Body>
              <h3 className="h5 mb-3 text-warning">Direct Connection & Referrals</h3>
              <p className="text-muted-soft mb-0">We serve as a trusted bridge between veterans and available services. We verify referrals when possible and follow up so veterans have support while navigating the system.</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="glass-card h-100 text-light">
            <Card.Body>
              <h3 className="h5 mb-3 text-warning">Fighting Food Insecurity</h3>
              <p className="text-muted-soft mb-0">We help address food insecurity through emergency assistance and community partnerships. By working with local food banks and organizations, we improve access to consistent meals and support.</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="glass-card h-100 text-light">
            <Card.Body>
              <h3 className="h5 mb-3 text-warning">Personalized Guidance</h3>
              <p className="text-muted-soft mb-0">We offer mentorship, peer support, and compassionate case guidance. Our safe, judgment-free environment helps veterans feel heard and respected.</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="glass-card h-100 text-light">
            <Card.Body>
              <h3 className="h5 mb-3 text-warning">Community & Partnership</h3>
              <p className="text-muted-soft mb-0">We build strong networks with local agencies, nonprofits, and businesses to amplify resources and help veterans access a broader range of support options.</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="glass-card text-light">
        <Card.Body>
          <h2 className="h4 mb-3">Our Impact</h2>
          <p className="text-muted-soft mb-3">
            Fremont Veterans Group exemplifies how grassroots organizations can profoundly impact the lives of veterans through compassion, practical support, and meaningful outreach. We don't just provide referrals, we actively combat hunger through direct food distribution, organize community
            support programs like "Adopt a Vet Christmas," and maintain ongoing relationships that remind veterans they are not forgotten.
          </p>
          <p className="text-muted-soft mb-0">By addressing fundamental needs and nurturing a sense of belonging, we honor the sacrifices made by those who served and help build stronger, healthier communities for all.</p>
        </Card.Body>
      </Card>
    </section>
  );
}

export default AboutPage;
